import { ReactElement } from "react";
import { Button } from "@twilio-paste/core/button";
import { ButtonGroup as PasteButtonGroup } from "@twilio-paste/core/button-group";

import { postAuthRedirect } from "@twiliointernal/e2-commons-shared-utils";
import {
  ButtonGroupBlock,
  ButtonValues,
  WiseOwlActionType,
  trackingDetails,
  WiseOwlElementIds,
  getOscConfig,
} from "@twiliointernal/e2-commons-shared-definitions";

import { SESSION_KEY } from "../definitions/constants";
import { useWiseOwlContext, useSessionStorage } from "../../../hooks";

const { hostName } = getOscConfig();

interface ButtonGroupRendererProps {
  element?: string;
  block: ButtonGroupBlock;
}

const ButtonGroup = ({
  element,
  block,
}: ButtonGroupRendererProps): ReactElement => {
  const {
    chatId,
    handlers: { handleRedirectAction, handleTrackEvent },
    applicationProps: { displayName, ingress },
  } = useWiseOwlContext();

  const [_, setinterruptedchatid] = useSessionStorage<string | null>(
    SESSION_KEY.INTERRUPTED_CHAT_ID,
    null
  );

  const handleButtonTrackingEvent = (buttonText: string): void => {
    if (handleTrackEvent) {
      handleTrackEvent(
        trackingDetails.wiseOwl.buttonActionClicked(displayName, buttonText),
        {
          chatId,
          ingress,
        }
      );
    }
  };

  const handleButtonAction = async (button: ButtonValues) => {
    const { action } = button;
    if (action.type === WiseOwlActionType.Redirect) {
      if (handleRedirectAction) {
        handleRedirectAction(action.values.redirectUrl);
      }
    }

    /**
     * Redirect to a deep link path and if
     * "persistChat" is "true", persist the chatId
     * in sessionStorage, so that the chat can be
     * resumed after the redirect.
     */
    if (action.type === WiseOwlActionType.DeepLink) {
      const { deepLinkPath, persistChat } = action.values;

      if (persistChat && chatId) {
        setinterruptedchatid(chatId);
      }
      await postAuthRedirect({
        authLink: `/${deepLinkPath}`,
        hostname: hostName,
      });
    }
    handleButtonTrackingEvent(button.text);
  };

  return (
    <PasteButtonGroup
      element={element || WiseOwlElementIds.WISE_OWL_BUTTON_GROUP_BLOCK}
    >
      {block.values.buttons.map((button) => (
        <Button
          variant="secondary"
          size="rounded_small"
          onClick={async () => await handleButtonAction(button)}
        >
          {button.text}
        </Button>
      ))}
    </PasteButtonGroup>
  );
};

export default ButtonGroup;
